import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import AuthShell from '../components/layout/AuthShell';
import AlertMessage from '../components/ui/AlertMessage';
import PasswordInput from '../components/ui/PasswordInput';
import PasswordRules from '../components/ui/PasswordRules';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [checking, setChecking] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [username, setUsername] = useState('');
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // A link érvényességét már betöltéskor ellenőrizzük, hogy a user ne
  // gépeljen be egy új jelszót egy lejárt linkre.
  useEffect(() => {
    if (!token) {
      setChecking(false);
      setError('This password reset link is invalid or has expired.');
      return;
    }
    api.get('/auth/reset-password', { params: { token } })
      .then(res => {
        setTokenValid(true);
        setUsername(res.data.username || '');
      })
      .catch(err => {
        setError(err.response?.data?.error || 'This password reset link is invalid or has expired.');
      })
      .finally(() => setChecking(false));
  }, [token]);

  const validate = () => {
    if (form.password !== form.confirm) return 'Passwords do not match.';
    if (form.password.length < 8) return 'Password must be at least 8 characters.';
    if (!/[A-Z]/.test(form.password)) return 'Password must contain at least one uppercase letter.';
    if (!/[0-9]/.test(form.password)) return 'Password must contain at least one number.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validErr = validate();
    if (validErr) { setError(validErr); return; }
    setError(null);
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password: form.password });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred while resetting your password.');
      // Csak akkor dobjuk el az űrlapot, ha maga a link szállt el — egy
      // jelszó-szabály hibánál a user tudjon újra próbálni.
      if (err.response?.data?.code === 'RESET_TOKEN_INVALID') setTokenValid(false);
    } finally {
      setLoading(false);
    }
  };

  const confirmMismatch = form.confirm.length > 0 && form.confirm !== form.password;

  if (checking) return <LoadingSpinner fullPage label="Checking your link…" />;

  if (done) {
    return (
      <AuthShell title="Password updated" subtitle="You can sign in with the new one">
        <AlertMessage msg={{
          type: 'success',
          text: 'Your password has been updated. All previous sessions — website and loader — were signed out. Redirecting you to the sign in page…',
        }} />
        <Link to="/login" className="btn-primary w-full py-3">Sign in now</Link>
      </AuthShell>
    );
  }

  if (!tokenValid) {
    return (
      <AuthShell title="Link expired" subtitle="This reset link can no longer be used">
        <AlertMessage msg={{
          type: 'error',
          text: error || 'This password reset link is invalid or has expired.',
        }} />
        <p className="text-sm text-gray-500 mb-6 leading-relaxed">
          Reset links expire after 30 minutes and can only be used once. Request a new one below.
        </p>
        <Link to="/forgot-password" className="btn-primary w-full py-3">Request a new link</Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Set a new password"
      subtitle={username ? `Signing in as ${username} after this` : 'Password reset'}
    >
      <AlertMessage msg={error ? { type: 'error', text: error } : null} />

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="label">New Password</label>
          <PasswordInput
            value={form.password}
            onChange={v => setForm(p => ({ ...p, password: v }))}
            autoComplete="new-password"
            required
          />
          <PasswordRules value={form.password} />
        </div>

        <div>
          <label className="label">Confirm New Password</label>
          <PasswordInput
            value={form.confirm}
            onChange={v => setForm(p => ({ ...p, confirm: v }))}
            autoComplete="new-password"
            required
            className={confirmMismatch ? 'border-red-500/50' : ''}
          />
          {confirmMismatch && <p className="text-xs text-red-400 mt-2">Passwords do not match.</p>}
        </div>

        <button type="submit" className="btn-primary w-full py-3" disabled={loading}>
          {loading ? 'Updating password…' : 'Update password'}
        </button>
      </form>

      <p className="text-center text-xs text-gray-600 mt-6 leading-relaxed">
        Changing your password signs out every active session, including the loader.
      </p>
    </AuthShell>
  );
}
